import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ListMusic, Plus, Search, Users, Music, Play, Trash2,
  Lock, Globe, UserPlus, Loader2, ChevronRight, Heart
} from 'lucide-react';

export default function CollabPlaylistsPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const headers = getAuthHeaders();

  const fetchPlaylists = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/playlists?limit=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      if (res.ok) setPlaylists((await res.json()).playlists || []);
    } catch {}
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchPlaylists(); }, [fetchPlaylists]);

  const fetchMyPlaylists = async () => {
    try {
      const res = await fetch(`${API}/playlists/my`, { headers });
      if (res.ok) setMyPlaylists((await res.json()).playlists || []);
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'mine' && user) fetchMyPlaylists();
  }, [activeTab]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/playlists/create`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName, description: newDesc, is_collaborative: true, is_public: true })
      });
      if (res.ok) {
        const d = await res.json();
        setShowCreate(false);
        setNewName('');
        setNewDesc('');
        navigate(`/playlists/${d.playlist.playlist_id}`);
      }
    } catch {}
    finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-20" data-testid="collab-playlists-page">
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="playlists-title">
                <div className="p-2 rounded-lg bg-gradient-to-br from-violet-600 to-purple-700"><ListMusic className="w-6 h-6" /></div>
                Collaborative Playlists
              </h1>
              <p className="text-muted-foreground text-sm mt-1">Create and collaborate on playlists with other users.</p>
            </div>
            {user && <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setShowCreate(true)} data-testid="create-playlist-btn"><Plus className="w-4 h-4 mr-1" /> New Playlist</Button>}
          </div>

          <div className="flex gap-2 mt-4">
            {['browse', 'mine'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`playlist-tab-${tab}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab ? 'bg-violet-600 text-white' : 'bg-muted/30 text-muted-foreground'}`}>
                {tab === 'browse' ? 'Browse All' : 'My Playlists'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-6">
        {/* Create Modal */}
        {showCreate && (
          <Card className="p-5 bg-card border-border mb-6" data-testid="create-playlist-form">
            <h3 className="font-semibold mb-3">Create New Playlist</h3>
            <div className="space-y-3">
              <Input placeholder="Playlist name..." value={newName} onChange={e => setNewName(e.target.value)} className="bg-muted/30 border-border" data-testid="playlist-name-input" />
              <Input placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} className="bg-muted/30 border-border" data-testid="playlist-desc-input" />
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="bg-violet-600" data-testid="create-playlist-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)} className="border-border">Cancel</Button>
              </div>
            </div>
          </Card>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search playlists..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-muted/30 border-border" data-testid="search-playlists" />
        </div>

        {/* Playlist Grid */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="playlists-grid">
            {(activeTab === 'mine' ? myPlaylists : playlists).length === 0 ? (
              <div className="col-span-full text-center py-8">
                <ListMusic className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
                <p className="text-muted-foreground">{activeTab === 'mine' ? 'No playlists yet. Create one!' : 'No playlists found.'}</p>
              </div>
            ) : (activeTab === 'mine' ? myPlaylists : playlists).map(pl => (
              <Card key={pl.playlist_id} className="bg-card border-border hover:border-border transition-all cursor-pointer overflow-hidden" data-testid={`playlist-${pl.playlist_id}`}
                onClick={() => navigate(`/playlists/${pl.playlist_id}`)}>
                <div className="h-24 bg-gradient-to-br from-violet-900/30 to-purple-900/20 flex items-center justify-center">
                  <ListMusic className="w-10 h-10 text-violet-400/50" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{pl.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{pl.owner_name || 'User'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {pl.is_collaborative && <Badge className="text-xs bg-violet-500/10 text-violet-400">Collab</Badge>}
                      {pl.is_public ? <Globe className="w-3.5 h-3.5 text-muted-foreground" /> : <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Music className="w-3 h-3" />{pl.track_count || 0} tracks</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(pl.collaborators || []).length}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
